// src/services/firebaseAuthService.js
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    updatePassword,
    deleteUser
  } from "firebase/auth";
import { 
    doc, 
    setDoc, 
    getDoc, 
    collection, 
    getDocs, 
    updateDoc, 
    deleteDoc, 
    query, 
    where 
  } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

// Benutzer anmelden
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Erweiterte Benutzerdaten aus Firestore holen
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      // Vollständige Benutzerdaten zurückgeben
      return {
        id: user.uid,
        email: user.email,
        ...userDoc.data()
      };
    } else {
      throw new Error("Benutzerprofil wurde nicht gefunden");
    }
  } catch (error) {
    console.error("Login-Fehler:", error.message);
    throw error;
  }
};

// Benutzer registrieren
export const registerUser = async (email, password, username, role = "user") => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Zusätzliche Benutzerdaten in Firestore speichern
    const userData = {
      username: username,
      role: role,
      email: email,
      createdAt: new Date(),
    };
    
    await setDoc(doc(db, "users", user.uid), userData);
    
    return {
      id: user.uid,
      email: user.email,
      ...userData
    };
  } catch (error) {
    console.error("Registrierungsfehler:", error.message);
    throw error;
  }
};

// Benutzer abmelden
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error("Logout-Fehler:", error.message);
    throw error;
  }
};

// Aktuellen Benutzer mit Firestore-Daten holen
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  
  if (!user) {
    return null;
  }
  
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return {
        id: user.uid,
        email: user.email,
        ...userDoc.data()
      };
    } else {
      console.warn("Benutzerprofil nicht gefunden für angemeldeten Benutzer");
      return {
        id: user.uid,
        email: user.email
      };
    }
  } catch (error) {
    console.error("Fehler beim Laden des aktuellen Benutzers:", error.message);
    throw error;
  }
};

// Alle Benutzer holen (Admin-Funktion)
export const getAllUsers = async () => {
  try {
    const usersCollectionRef = collection(db, "users");
    const querySnapshot = await getDocs(usersCollectionRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error("Fehler beim Laden aller Benutzer:", error.message);
    throw error;
  }
};

// Benutzerrolle aktualisieren
export const updateUserRole = async (userId, newRole) => {
  try {
    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, {
      role: newRole,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Benutzerrolle:", error.message);
    throw error;
  }
};

// Benutzername aktualisieren
export const updateUsername = async (userId, newUsername, newEmail, newRole) => {
  try {
    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, {
      username: newUsername,
      email: newEmail,
      role: newRole,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Benutzerdaten:", error.message);
    throw error;
  }
};

// Passwort zurücksetzen (Link senden)
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error("Fehler beim Senden der Passwort-Zurücksetzung:", error.message);
    throw error;
  }
};

// Passwort für einen Benutzer ändern
export const changePassword = async (userId, newPassword) => {
  try {
    // Benutzer in Firebase Auth aktualisieren
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Kein Benutzer angemeldet");
    }
    
    // Prüfen, ob der aktuelle Benutzer Admin oder SuperAdmin ist
    const currentUserData = await getCurrentUser();
    if (currentUserData.role !== "admin" && currentUserData.role !== "superadmin") {
      throw new Error("Nur Administratoren können Passwörter ändern");
    }
    
    // Passwort aktualisieren
    await updatePassword(user, newPassword);
    
    return true;
  } catch (error) {
    console.error("Fehler beim Ändern des Passworts:", error);
    throw error;
  }
};

// Benutzer löschen
export const deleteUserAccount = async (userId) => {
  try {
    // Firestore-Dokument löschen
    const userDocRef = doc(db, "users", userId);
    await deleteDoc(userDocRef);
    
    // Wenn der aktuelle Benutzer seinen eigenen Account löscht
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.uid === userId) {
      await deleteUser(currentUser);
    }
    
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Benutzers:", error.message);
    throw error;
  }
};

// Prüfen, ob ein Benutzername bereits existiert
export const checkUsernameExists = async (username) => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("username", "==", username));
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Fehler bei der Überprüfung des Benutzernamens:", error.message);
    throw error;
  }
};

// SuperAdmin erstellen, falls keiner existiert
export const ensureSuperAdminExists = async (email, password, username = "admin") => {
  try {
    // Prüfen, ob bereits ein SuperAdmin existiert
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "superadmin"));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      console.log("SuperAdmin existiert bereits");
      return false;
    }
    
    // Wenn kein SuperAdmin existiert, einen erstellen
    const superAdmin = await registerUser(email, password, username, "superadmin");
    console.log("SuperAdmin erstellt:", superAdmin);
    return true;
  } catch (error) {
    console.error("Fehler beim Erstellen des SuperAdmins:", error.message);
    throw error;
  }
};

// Offene Freiwunsch-/Urlaubsanträge zählen (status == 'pending')
export const getOpenTimeOffRequestsCount = async () => {
  try {
    const requestsRef = collection(db, 'timeOffRequests');
    
    // Hole die aktuelle Projekt-ID aus dem localStorage
    const projektId = localStorage.getItem('lastProjektId') || '';
    let projektName = projektId;
    
    // Hole den Projektnamen aus dem localStorage
    try {
      const projekteRaw = localStorage.getItem('projekte');
      if (projekteRaw) {
        const projekte = JSON.parse(projekteRaw);
        const found = projekte.find(p => p.id === projektId);
        if (found) projektName = found.name;
      }
    } catch (e) { /* ignore */ }
    
    // Erstelle die Query mit Projekt- und Statusfilter
    const q = query(
      requestsRef,
      where('status', '==', 'pending'),
      where('projektId', '==', projektName)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Fehler beim Laden der offenen Anträge:', error.message);
    throw error;
  }
};

// Alle Freiwunsch-/Urlaubsanträge laden
export const getAllTimeOffRequests = async () => {
  try {
    const requestsRef = collection(db, 'timeOffRequests');
    
    // Hole die aktuelle Projekt-ID aus dem localStorage
    const projektId = localStorage.getItem('lastProjektId') || '';
    let projektName = projektId;
    
    // Hole den Projektnamen aus dem localStorage
    try {
      const projekteRaw = localStorage.getItem('projekte');
      if (projekteRaw) {
        const projekte = JSON.parse(projekteRaw);
        const found = projekte.find(p => p.id === projektId);
        if (found) projektName = found.name;
      }
    } catch (e) { /* ignore */ }
    
    // Hole den aktuellen Benutzer
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Kein Benutzer angemeldet');
    }
    
    // Erstelle die Query ohne Projektfilter (alle Anträge laden)
    const q = query(requestsRef);
    const querySnapshot = await getDocs(q);
    
    const requests = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      requests.push({ 
        id: doc.id, 
        ...data,
        rejectionReason: data.rejectionReason || null
      });
    });
    
    return requests;
  } catch (error) {
    console.error('Fehler beim Laden der Anträge:', error.message);
    throw error;
  }
};

// Antrag löschen
export const deleteTimeOffRequest = async (requestId) => {
  try {
    await deleteDoc(doc(db, 'timeOffRequests', requestId));
    return true;
  } catch (error) {
    console.error('Fehler beim Löschen des Antrags:', error.message);
    throw error;
  }
};